import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PageSEO from "@/components/PageSEO";
import { ARTICLES_BY_SLUG, ARTICLES } from "@/lib/articles";
import { ArrowLeft, ArrowUpRight } from "lucide-react";

function renderBody(body) {
  // Lightweight markdown-lite renderer: handles ## headings, paragraphs, and lists with "-"
  const blocks = body.split(/\n\n+/);
  return blocks.map((block, i) => {
    const t = block.trim();
    if (t.startsWith("## ")) {
      return (
        <h2 key={i} className="font-serif-display text-3xl md:text-4xl text-stone-900 mt-12 mb-5 leading-tight">
          {t.replace(/^##\s+/, "")}
        </h2>
      );
    }
    if (t.startsWith("### ")) {
      return (
        <h3 key={i} className="font-serif-display text-2xl text-stone-900 mt-8 mb-3 leading-tight">
          {t.replace(/^###\s+/, "")}
        </h3>
      );
    }
    if (t.startsWith("- ")) {
      const items = t.split("\n").map((line) => line.replace(/^-\s+/, ""));
      return (
        <ul key={i} className="list-disc ml-6 my-4 space-y-2 text-stone-700">
          {items.map((it, j) => (
            <li key={j} dangerouslySetInnerHTML={{ __html: formatInline(it) }} />
          ))}
        </ul>
      );
    }
    return (
      <p
        key={i}
        className="text-stone-700 text-lg leading-relaxed my-4"
        dangerouslySetInnerHTML={{ __html: formatInline(t) }}
      />
    );
  });
}
function formatInline(s) {
  // 1) bold: **texto** -> <strong>
  let out = s.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  // 2) markdown links: [texto](url) -> <a>
  //    External (http/https) -> target=_blank + rel noopener
  //    Internal (starts with /) -> normal <a> (SEO-friendly; full nav)
  out = out.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_m, text, url) => {
    const isExternal = /^https?:\/\//i.test(url);
    const base =
      'class="text-[#E07A5F] underline underline-offset-4 decoration-stone-300 hover:decoration-[#E07A5F] transition-colors"';
    if (isExternal) {
      return `<a href="${url}" ${base} target="_blank" rel="noopener nofollow">${text}</a>`;
    }
    return `<a href="${url}" ${base}>${text}</a>`;
  });
  return out;
}

export default function BlogPost() {
  const { slug } = useParams();
  const article = ARTICLES_BY_SLUG[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [article]);

  if (!article) return <Navigate to="/blog" replace />;

  const related = ARTICLES.filter((a) => a.slug !== slug).slice(0, 3);
  const heroAbs = article.hero.startsWith("http")
    ? article.hero
    : `https://puravidatuxpan.com${article.hero}`;

  return (
    <div data-testid="blog-post-page" className="min-h-screen bg-[#FAFAF9] text-stone-900">
      <PageSEO
        title={article.title}
        description={article.meta_description}
        keywords={(article.keywords || []).join(", ")}
        path={`/blog/${slug}`}
        image={heroAbs}
        imageAlt={article.title}
        type="article"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: article.title,
          description: article.meta_description,
          image: heroAbs,
          datePublished: article.date,
          dateModified: article.date,
          author: {
            "@type": "Organization",
            name: "Villas Pura Vida",
            url: "https://puravidatuxpan.com",
          },
          publisher: {
            "@type": "Organization",
            name: "Villas Pura Vida",
            url: "https://puravidatuxpan.com",
            logo: {
              "@type": "ImageObject",
              url: "https://puravidatuxpan.com/favicon.svg",
            },
          },
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": `https://puravidatuxpan.com/blog/${slug}`,
          },
          keywords: (article.keywords || []).join(", "),
          articleSection: article.category,
        }}
      />      <Header variant="home" />

      <article className="pt-28 md:pt-36">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <Link to="/blog" className="inline-flex items-center gap-2 text-eyebrow text-stone-500 hover:text-[#E07A5F] mb-8 transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> Blog
          </Link>
          <p className="text-eyebrow text-[#E07A5F] mb-3">
            {article.category} · {article.read_time}
          </p>
          <h1 className="font-serif-display text-4xl md:text-6xl leading-[1] tracking-tight text-stone-900 mb-6">
            {article.title}
          </h1>
          <p className="text-stone-600 text-lg leading-relaxed mb-10">{article.meta_description}</p>
        </div>

        <div className="max-w-5xl mx-auto px-6 md:px-10 mb-12">
          <div className="aspect-[16/9] overflow-hidden bg-stone-200">
            <img src={article.hero} alt={article.title} className="w-full h-full object-cover" />
          </div>
        </div>

        <div data-testid="blog-body" className="max-w-3xl mx-auto px-6 md:px-10 pb-16">
          {renderBody(article.body)}
        </div>
      </article>

      <section className="bg-stone-100 py-20 border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <h2 className="font-serif-display text-3xl md:text-4xl mb-10">Continúa leyendo</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((a) => (
              <Link
                key={a.slug}
                to={`/blog/${a.slug}`}
                className="group block bg-white border border-stone-200 hover:border-stone-400 transition-all hover:-translate-y-1"
              >
                <div className="aspect-[5/4] overflow-hidden bg-stone-200">
                  <img src={a.hero} alt={a.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1500ms]" />
                </div>
                <div className="p-5">
                  <div className="flex justify-between items-start gap-2">
                    <h3 className="font-serif-display text-xl leading-tight group-hover:text-[#E07A5F] transition-colors">{a.title}</h3>
                    <ArrowUpRight className="w-4 h-4 text-stone-400 group-hover:text-[#E07A5F] flex-shrink-0" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
